import { AppEntitlementValue, CatalogEntryValue, expandAppEntitlementView } from 'lib/actions/apps/app-entitlement'
import { AppResourceValue, expandAppResourceView } from 'lib/actions/apps/app-resources'
import { expandRequestCatalogView, RequestCatalogValue } from 'lib/actions/request_catalog'
import { expandEntitlementPerAppView } from 'lib/actions/request_catalog/request-catalog-search'
import { expandValue } from 'lib/rpcts'
import { App } from 'pbts/c1/api/app/v1/app'
import { AppEntitlement, CatalogEntry } from 'pbts/c1/api/app/v1/app_entitlement'
import { AppEntitlementRef } from 'pbts/c1/api/app/v1/app_refs'
import { AppResource, AppResourceType } from 'pbts/c1/api/app/v1/app_resource'
import {
    AppEntitlementsInCatalogTermsResponse,
    AppEntitlementWithRequestCatalogs,
    AppEntitlementWithRequestCatalogsRef,
    AppWithGrantStatus,
    GetAppEntitlementWithRequestCatalogsResponse,
    RequestCatalogAppEntitlementBindingRef,
    SearchAppResourcesInCatalogResponse,
} from 'pbts/c1/api/requestcatalog/v1/requestcatalog'
import { RequestCatalogSearchServiceClient } from 'pbts/c1/api/requestcatalog/v1/requestcatalog.client'
import { BundleAutomationRef, RequestCatalogRef } from 'pbts/c1/api/requestcatalog/v1/requestcatalogmanagement'
import { RequestCatalogManagementServiceClient } from 'pbts/c1/api/requestcatalog/v1/requestcatalogmanagement.client'
import { FacetValue } from 'pbts/c1/api/search/v1/facet'
import { TaskCountRefreshRef, TaskRef } from 'pbts/c1/api/task/v1/task'
import { User } from 'pbts/c1/api/user/v1/user'
import { Any } from 'pbts/google/protobuf/any'
import { Timestamp } from 'pbts/google/protobuf/timestamp'
import { Code as pbCode } from 'pbts/google/rpc/code'
import { Status as pbStatus } from 'pbts/google/rpc/status'
import { RequestCatalogUserEntitlementPerAppValue } from 'redux_toolkit/browse-access'
import { createMutationApi } from 'redux_toolkit/lib/createMutationApi'
import { createPagedListApi } from 'redux_toolkit/lib/createPagedListApi'
import { createUnaryApi } from 'redux_toolkit/lib/createUnaryApi'
import { transformAppEntitlementValueArrayResponse } from './entitlements'

const tag = RequestCatalogRef.typeName
const appEntitlementsTag = AppEntitlementRef.typeName

const transformFacetResponse = (d: AppEntitlementsInCatalogTermsResponse): Array<FacetValue> => {
    const item = d?.facets?.facets?.[0]?.item
    return item?.oneofKind === 'value' ? item.value?.values : []
}

const transformRequestCatalogView = (rc): RequestCatalogValue =>
    expandRequestCatalogView(rc.requestCatalogView, rc.expanded)

export const requestCatalogGetApi = createUnaryApi(
    RequestCatalogManagementServiceClient,
    'get',
    transformRequestCatalogView,
    tag,
)

export const listEntitlementsForAccessApi = createPagedListApi(
    RequestCatalogManagementServiceClient,
    'listEntitlementsForAccess',
    transformAppEntitlementValueArrayResponse,
    appEntitlementsTag,
)

export const deleteRequestCatalogApi = createMutationApi(
    RequestCatalogManagementServiceClient,
    'delete',
    transformRequestCatalogView,
    tag,
)

export const updateRequestCatalogApi = createMutationApi(
    RequestCatalogManagementServiceClient,
    'update',
    transformRequestCatalogView,
    tag,
)

export const addAccessEntitlementsApi = createMutationApi(
    RequestCatalogManagementServiceClient,
    'addAccessEntitlements',
    transformRequestCatalogView,
    appEntitlementsTag,
)

export const removeAccessEntitlementsApi = createMutationApi(
    RequestCatalogManagementServiceClient,
    'removeAccessEntitlements',
    transformRequestCatalogView,
    appEntitlementsTag,
)

export const requestCatalogsSearchApi = createPagedListApi(
    RequestCatalogSearchServiceClient,
    'searchRequestCatalogs',
    undefined,
    RequestCatalogRef.typeName,
)

export const requestCatalogsSearchWithEnrollmentCountsApi = createPagedListApi(
    RequestCatalogSearchServiceClient,
    'searchRequestCatalogsWithEnrollmentCounts',
    undefined,
    RequestCatalogRef.typeName,
)

const transformAppEntitlementWithRequestCatalogsValueRow = (resp): Array<AppEntitlementWithRequestCatalogsValue> =>
    resp.list.map((v): AppEntitlementWithRequestCatalogsValue => ({ id: v.entitlement?.id, appId: v.entitlement?.appId, ...v }))

export interface AppEntitlementWithRequestCatalogsValue extends AppEntitlementWithRequestCatalogs {
    id: string
    appId: string
}

export const entitlementsWithRequestCatalogsSearchApi = createPagedListApi(
    RequestCatalogSearchServiceClient,
    'searchAppEntitlementsWithRequestCatalogs',
    transformAppEntitlementWithRequestCatalogsValueRow,
    AppEntitlementRef.typeName,
)

export const getEntitlementWithRequestCatalogsApi = createUnaryApi(
    RequestCatalogSearchServiceClient,
    'getAppEntitlementWithRequestCatalogs',
    (resp: GetAppEntitlementWithRequestCatalogsResponse): AppEntitlementWithRequestCatalogs => {
        return resp.entitlement ?? AppEntitlementWithRequestCatalogs.create({})
    },
    AppEntitlementWithRequestCatalogsRef.typeName,
)

const transformToApp = (resp: { list: Array<AppWithGrantStatus> }): Array<App> => resp.list.map((v) => App.create(v.app ?? {}))

export const searchAppsWithGrantStatusApi = createPagedListApi(
    RequestCatalogSearchServiceClient,
    'searchAppsWithGrantStatus',
    transformToApp,
)

/**
 * @deprecated Use searchEntitlementsPerAppInCatalogApi instead. This RPC isn't actually paginated.
 */
export const deprecatedSearchEntitlementsPerAppInCatalogApi = createPagedListApi(
    RequestCatalogSearchServiceClient,
    'searchEntitlementsPerApp',
    (resp): Array<RequestCatalogUserEntitlementPerAppValue> =>
        resp.list.map((v) => expandEntitlementPerAppView(v, resp.expanded)),
)

export const searchEntitlementsPerAppInCatalogApi = createPagedListApi(
    RequestCatalogSearchServiceClient,
    'searchCatalogEntitlementsPerApp',
    (resp): Array<AppEntitlementValue> => resp.list.map((v) => expandAppEntitlementView(v, resp.expanded)),
)

export const addAppEntitlementsRequestCatalogsApi = createMutationApi(
    RequestCatalogManagementServiceClient,
    'addAppEntitlements',
)
export const removeAppEntitlementsRequestCatalogsApi = createMutationApi(
    RequestCatalogManagementServiceClient,
    'removeAppEntitlements',
)

export const ListAllEntitlementIdsPerApp = createUnaryApi(
    RequestCatalogManagementServiceClient,
    'listAllEntitlementIdsPerApp',
    (resp): Array<AppEntitlementRef> => resp.refs,
)

export const listRequestCatalogsApi = createPagedListApi(
    RequestCatalogManagementServiceClient,
    'list',
    (resp): Array<RequestCatalogValue> => resp.list.map((v): RequestCatalogValue => {
        return expandRequestCatalogView(v, resp.expanded)
    }),
)

export const searchAppResourcesTasksApi = createPagedListApi(
    RequestCatalogSearchServiceClient,
    'searchTasksForResources',
    undefined,
    TaskRef.typeName,
)

export const arumApi = createUnaryApi(RequestCatalogSearchServiceClient, 'parseAccessRequestToken')

export const arumGenerateApi = createMutationApi(RequestCatalogSearchServiceClient, 'generateAccessRequestToken')

const expandCatalogEntry = (msg: CatalogEntry | undefined, values: Any[]): CatalogEntryValue => {
    let app = expandValue<App>(msg?.appPath || '', App, values)
    if (app === undefined) {
        const appErr = expandValue<pbStatus>(msg?.appPath || '', pbStatus, values)
        if (appErr?.code === pbCode.NOT_FOUND) {
            app = App.create({
                displayName: 'Deleted',
            })
        }
    }

    if (app?.deletedAt) {
        app.displayName += ' [Deleted]'
    }

    return {
        ...AppEntitlement.create(msg?.appEntitlement ?? {}),
        app,
        appResourceType: expandValue<AppResourceType>(msg?.appResourceTypePath || '', AppResourceType, values),
        appResource: expandValue<AppResource>(msg?.appResourcePath || '', AppResource, values),
        enrolledMembersCount: msg?.enrolledMembersCount,
    }
}

export const searchEntitlementsInCatalogApi = createPagedListApi(
    RequestCatalogSearchServiceClient,
    'searchAppEntitlementsInCatalog',
    (resp) => resp.list.map((v): CatalogEntryValue => expandCatalogEntry(v, resp.expanded)),
    RequestCatalogAppEntitlementBindingRef.typeName,
)

export const searchRemovedUsersApi = createPagedListApi(
    RequestCatalogSearchServiceClient,
    'searchRemovedUsers',
    (resp): Array<User> => resp.list.map((v) => User.create(v)),
)

export const countUsersLosingMembershipApi = createUnaryApi(
    RequestCatalogSearchServiceClient,
    'countUsersLosingMembership',
    undefined,
)

export const SearchAppResourcesInCatalogApi = createPagedListApi(
    RequestCatalogSearchServiceClient,
    'searchAppResourcesInCatalog',
    (resp: SearchAppResourcesInCatalogResponse): Array<AppResourceValue> => resp
        .list
        .map((v): AppResourceValue => expandAppResourceView(v, resp.expanded)),

)

export const entitlementsInCatalogTermsApi = createUnaryApi(RequestCatalogSearchServiceClient, 'getSearchTerms', transformFacetResponse)

export const getBundleAutomationApi = createUnaryApi(
    RequestCatalogManagementServiceClient,
    'getBundleAutomation',
    undefined,
    BundleAutomationRef.typeName,
)

export const setBundleAutomationApi = createMutationApi(
    RequestCatalogManagementServiceClient,
    'setBundleAutomation',
    undefined,
    BundleAutomationRef.typeName,
)

export const forceRunBundleAutomationApi = createMutationApi(
    RequestCatalogManagementServiceClient,
    'forceRunBundleAutomation',
)

export const resumePausedBundleAutomationApi = createMutationApi(
    RequestCatalogManagementServiceClient,
    'resumePausedBundleAutomation',
)

export type AccessProfileMembershipValue = {
    accessProfile: AppEntitlement
    user: User
    addedAt?: Timestamp
    addedVia: { derivedIdType: string }[]
}

export const searchAccessProfileMembershipsApi = createPagedListApi(
    RequestCatalogSearchServiceClient,
    'searchAccessProfileMemberships',
    (resp): Array<AccessProfileMembershipValue> => resp.list.map((v) => ({
        accessProfile: AppEntitlement.create(v.accessProfile ?? {}),
        user: User.create(v.user ?? {}),
        addedAt: v.addedAt,
        addedVia: v.addedVia,
    })),
)

export const searchCatalogEnrollmentsCountApi = createUnaryApi(
    RequestCatalogSearchServiceClient,
    'searchCatalogEnrollmentsCount',
    undefined,
    TaskCountRefreshRef.typeName,
)

export const searchCatalogAccessEntitlementsApi = createPagedListApi(
    RequestCatalogSearchServiceClient,
    'searchCatalogAccessEntitlements',
    (resp): Array<AppEntitlementValue> => resp.list.map((v) => expandAppEntitlementView(v, resp.expanded)),
    AppEntitlementRef.typeName,
)

export const createAppDefaultCatalogApi = createMutationApi(
    RequestCatalogManagementServiceClient,
    'createAppDefaultCatalog',
    transformRequestCatalogView,
    RequestCatalogRef.typeName,
)

export const addAppEntitlementToAccessProfileApi = createMutationApi(
    RequestCatalogManagementServiceClient,
    'addAppEntitlements',
    undefined,
)
